/* eslint-disable @atlaskit/design-system/use-tokens-typography */
/* eslint-disable @repo/internal/react/require-jsdoc */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type ReactNode } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled
import { css, jsx } from '@emotion/react';

import { token } from '@atlaskit/tokens';

const rootStyles = css({
	margin: 0,
	fontFamily:
		'var(--ds-font-family-brand-heading, "Charlie Display", ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", Ubuntu, "Helvetica Neue", sans-serif)',
});

const sizeMap = {
	// H6 WPL
	xsmall: css({
		fontSize: '1rem', // 16px
		fontWeight: '700',
		lineHeight: '1.25rem', // 20px
	}),
	// H5 WPL
	small: css({
		fontSize: '1.125rem', // 18px
		fontWeight: '700',
		lineHeight: '1.375rem', // 22px
		'@media (min-width: 30rem)': {
			fontSize: '1.125rem', // 18px
			fontWeight: '700',
			lineHeight: '1.375rem', // 22px
		},
		'@media (min-width: 48rem)': {
			fontSize: '1.25rem', // 20px
			fontWeight: '700',
			lineHeight: '1.5rem', // 24px
		},
	}),
	// H4 WPL
	medium: css({
		fontSize: '1.25rem', // 20px
		fontWeight: '700',
		lineHeight: '1.5rem', // 24px
		'@media (min-width: 30rem)': {
			fontSize: '1.375rem', // 22px
			fontWeight: '700',
			lineHeight: '1.75rem', // 28px
		},
		'@media (min-width: 48rem)': {
			fontSize: '1.5rem', // 24px
			fontWeight: '700',
			lineHeight: '1.75rem', // 28px
		},
	}),
	// H3 WPL
	large: css({
		fontSize: '1.5rem', // 24px
		fontWeight: '700',
		lineHeight: '1.75rem', // 28px
		'@media (min-width: 30rem)': {
			fontSize: '1.75rem', // 28px
			fontWeight: '700',
			lineHeight: '2.125rem', // 34px
		},
		'@media (min-width: 48rem)': {
			fontSize: '2rem', // 32px
			fontWeight: '700',
			lineHeight: '2.25rem', // 36px
		},
	}),
	// H2 WPL
	xlarge: css({
		fontSize: '2rem', // 32px
		fontWeight: '700',
		lineHeight: '2.25rem', // 36px
		'@media (min-width: 30rem)': {
			fontSize: '2.25rem', // 36px
			fontWeight: '700',
			lineHeight: '2.5rem', // 40px
		},
		'@media (min-width: 48rem)': {
			fontSize: '2.5rem', // 40px
			fontWeight: '700',
			lineHeight: '2.75rem', // 44px
		},
	}),
	// H1 small WPL
	xxlarge: css({
		fontSize: '2.5rem', // 40px
		fontWeight: '700',
		lineHeight: '2.75rem', // 44px
		'@media (min-width: 30rem)': {
			fontSize: '2.75rem', // 44px
			fontWeight: '700',
			lineHeight: '3rem', // 48px
		},
		'@media (min-width: 48rem)': {
			fontSize: '3rem', // 48px
			fontWeight: '700',
			lineHeight: '3.25rem', // 52px
		},
	}),
	// H1 large WPL (with custom font-weight)
	hero: css({
		fontSize: '3rem', // 48px
		fontWeight: '700',
		lineHeight: '3.25rem', // 52px
		'@media (min-width: 30rem)': {
			fontSize: '3.25rem', // 52px
			fontWeight: '700',
			lineHeight: '3.5rem', // 56px
		},
		'@media (min-width: 48rem)': {
			fontSize: '4.25rem', // 68px
			fontWeight: '700',
			lineHeight: '4.75rem', // 76px
		},
	}),
} as const;

const colorMap = {
	'color.text': css({ color: token('color.text') }),
	'color.text.inverse': css({ color: token('color.text.inverse') }),
};

interface BrandHeadingProps {
	size: keyof typeof sizeMap;
	as: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
	id?: string;
	color?: 'color.text' | 'color.text.inverse';
	children: ReactNode;
}
export const BrandHeading = ({
	size,
	as,
	id,
	color = 'color.text',
	children,
}: BrandHeadingProps) => {
	const Component = as;
	const sizeStyles = sizeMap[size];
	const colorStyles = colorMap[color];
	return (
		<Component id={id} css={[rootStyles, sizeStyles, colorStyles]}>
			{children}
		</Component>
	);
};
